import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const EASE = [0.2, 0, 0, 1] as const;

/**
 * How long a "remember me" session lasts, for the hint under the box.
 * Mirrors the backend's `REMEMBER_ME_DAYS` setting (fixed expiry from
 * the login, not sliding) — the response also carries the live value as
 * `refresh_expires_in_seconds`, but the hint renders before any login.
 */
const REMEMBER_ME_DAYS = 30;

const formContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
};

const formChild: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: EASE },
  },
};

interface LocationState {
  from?: string;
}

/**
 * Decide where to send the user after a successful login (or on the
 * already-logged-in guard). Centralised so LoginPage and the guard
 * can't drift apart.
 *
 * | Role | Lender review_status | Lands on |
 * |---|---|---|
 * | debtor | (n/a) | `/min-sag` |
 * | lender_human | `pending_review` or `rejected` | `/pending-approval` |
 * | lender_human | `approved` or unknown | `/entries` |
 * | operator | (n/a) | `/entries` |
 */
function computeHome(user: import('../store/authStore').AuthUser | null): string {
  if (!user) return '/entries';
  if (user.role === 'debtor') return '/min-sag';
  if (
    user.role === 'lender_human' &&
    user.lender_review_status &&
    user.lender_review_status !== 'approved'
  ) {
    return '/pending-approval';
  }
  return '/entries';
}

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const loading = useAuthStore((s) => s.loading);
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [remember, setRemember] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Detect Caps Lock from any password keypress so we can warn —
  // typing in caps is the most common cause of "wrong password"
  // when the user is sure they got it right.
  const handlePasswordKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const native = e.nativeEvent as unknown as { getModifierState?: (k: string) => boolean };
    if (typeof native.getModifierState === 'function') {
      setCapsLockOn(native.getModifierState('CapsLock'));
    }
  };

  // If the user is already logged in, drop them on their natural home
  // directly. Routing rules:
  // - Debtors → /min-sag.
  // - Lender_human whose lender is NOT yet approved → /pending-approval
  //   (covers self-signed lenders waiting on operator review).
  // - Everyone else → /entries (the canonical register inventory).
  // The guard avoids a re-login loop if they navigate back to /login.
  if (token) {
    const home = computeHome(user);
    const target = (location.state as LocationState | null)?.from ?? home;
    return <Navigate to={target} replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError(t('auth.requiredFields'));
      return;
    }

    try {
      await login(email.trim().toLowerCase(), password, remember);
      // Read the freshly-set user via getState() — relying on the
      // closure-captured `user` here would be stale, since React hasn't
      // re-rendered yet.
      //
      // Routing rules — see ``computeHome`` below for the full table.
      // If the user came in via a ProtectedRoute redirect, honour
      // their original destination via ``state.from``.
      const freshUser = useAuthStore.getState().user;
      const home = computeHome(freshUser);
      const fromState = (location.state as LocationState | null)?.from;
      const target = fromState ?? home;
      navigate(target, { replace: true });
    } catch (err) {
      // Backend contract: 401 with `{error_code: "invalid_credentials"}`
      // for bad creds. We render the same operator-friendly message for
      // any failure to avoid leaking provisioning details.
      if (axios.isAxiosError(err)) {
        setError(t('auth.invalidCredentials'));
      } else {
        setError(t('auth.invalidCredentials'));
      }
    }
  };

  return (
    <div className="space-y-12 max-w-[440px]">
      <motion.header
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: EASE }}
      >
        <div className="section-label mb-3">{t('auth.regOfficeLabel')}</div>
        <h1 className="font-mono text-2xl tracking-tight text-[var(--color-ink)]">
          {t('auth.loginTitle')}
        </h1>
      </motion.header>

      <motion.form
        onSubmit={handleSubmit}
        className="rule pt-8 space-y-6"
        variants={formContainer}
        initial="hidden"
        animate="visible"
        noValidate
      >
        <motion.div variants={formChild}>
          <label htmlFor="login-email" className="section-label block mb-2">
            {t('auth.emailLabel')}
          </label>
          <div className="relative w-full">
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(null);
              }}
              onFocus={() => setEmailFocused(true)}
              onBlur={() => setEmailFocused(false)}
              autoComplete="username"
              spellCheck={false}
              className="w-full"
              disabled={loading}
            />
            <motion.span
              aria-hidden="true"
              className="pointer-events-none absolute left-0 right-0 bottom-0 h-[2px] bg-[var(--color-primary)] origin-left"
              initial={false}
              animate={{ scaleX: emailFocused ? 1 : 0 }}
              transition={{ duration: 0.22, ease: EASE }}
              style={{ transformOrigin: '0% 50%' }}
            />
          </div>
        </motion.div>

        <motion.div variants={formChild}>
          <div className="flex items-baseline justify-between mb-2">
            <label htmlFor="login-password" className="section-label">
              {t('auth.passwordLabel')}
            </label>
            <Link
              to="/forgot-password"
              className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-primary)] hover:text-[var(--color-primary-strong)] transition-colors"
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>
          <div className="relative w-full">
            <input
              id="login-password"
              type={passwordVisible ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError(null);
              }}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => {
                setPasswordFocused(false);
                setCapsLockOn(false);
              }}
              onKeyDown={handlePasswordKeyDown}
              onKeyUp={handlePasswordKeyDown}
              autoComplete="current-password"
              spellCheck={false}
              className="w-full pr-10"
              disabled={loading}
            />
            <button
              type="button"
              onClick={() => setPasswordVisible((v) => !v)}
              aria-label={passwordVisible ? t('auth.hidePassword') : t('auth.showPassword')}
              aria-pressed={passwordVisible}
              tabIndex={-1}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] transition-colors"
            >
              {passwordVisible ? (
                <EyeOff className="w-4 h-4" strokeWidth={1.75} />
              ) : (
                <Eye className="w-4 h-4" strokeWidth={1.75} />
              )}
            </button>
            <motion.span
              aria-hidden="true"
              className="pointer-events-none absolute left-0 right-0 bottom-0 h-[2px] bg-[var(--color-primary)] origin-left"
              initial={false}
              animate={{ scaleX: passwordFocused ? 1 : 0 }}
              transition={{ duration: 0.22, ease: EASE }}
              style={{ transformOrigin: '0% 50%' }}
            />
          </div>
          <AnimatePresence>
            {capsLockOn && (
              <motion.div
                key="caps-lock"
                initial={{ opacity: 0, y: -2 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="mt-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-alert)]"
              >
                <span className="inline-block w-1 h-1 rounded-full bg-[var(--color-alert)]" />
                {t('auth.capsLockOn')}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* "Remember me" — off by default. Ticked, the backend also hands
            the SPA a revocable refresh token so the session survives the
            60-minute access token for REMEMBER_ME_DAYS. Native checkbox,
            hairline styling only; the label is a 44px tap target. */}
        <motion.div variants={formChild}>
          <label
            htmlFor="login-remember"
            className="flex items-center gap-3 min-h-11 cursor-pointer select-none"
          >
            <input
              id="login-remember"
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              disabled={loading}
              className="h-4 w-4 shrink-0 accent-[var(--color-primary)]"
            />
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-ink-soft)]">
              {t('auth.rememberMe')}
            </span>
          </label>
          <p className="text-xs text-[var(--color-ink-muted)] leading-relaxed">
            {t('auth.rememberMeHint', { days: REMEMBER_ME_DAYS })}
          </p>
        </motion.div>

        <motion.div variants={formChild} className="flex items-center gap-3">
          <motion.button
            type="submit"
            disabled={loading}
            className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            animate={
              loading
                ? { opacity: [1, 0.7, 1] }
                : { opacity: 1 }
            }
            transition={
              loading
                ? { duration: 1.2, repeat: Infinity, ease: 'easeInOut' }
                : { duration: 0.15 }
            }
          >
            <LogIn className="w-4 h-4" strokeWidth={1.5} />
            {loading ? t('auth.signingIn') : t('auth.signIn')}
          </motion.button>
        </motion.div>

        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              key={error}
              role="alert"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -2 }}
              transition={{ duration: 0.22, ease: EASE }}
              className="border border-[var(--color-alert)] bg-[var(--color-alert-soft)] px-4 py-3"
            >
              <span className="font-mono text-xs text-[var(--color-alert)] uppercase tracking-wider">
                {error}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.form>

      {/* Inline CTA for creditors who don't yet have an account.
          One link, no redesign — they self-sign at /signup-creditor
          and the operator approves them out-of-band. */}
      <div className="text-xs font-mono text-[var(--color-ink-muted)] space-y-1">
        <div>
          <Link
            to="/signup-creditor"
            className="text-[var(--color-primary)] hover:text-[var(--color-primary-strong)]"
          >
            {t('auth.creditorSelfSignupCta')}
          </Link>
        </div>
        {/* Debtor self-service entry. Debtors don't have email +
            password — they auth with case_number + CPR on the
            dedicated /debtor-login page. The link sits below the
            creditor CTA so the page isn't crowded. */}
        <div>
          <Link
            to="/debtor-login"
            className="text-[var(--color-primary)] hover:text-[var(--color-primary-strong)]"
          >
            {t('auth.debtorLoginCta')}
          </Link>
        </div>
      </div>

    </div>
  );
}
